import * as React from 'react';
import AuthLayoutComponent from './auth-layout.component';
import { isValidEmail } from '../util/Validation';
import { AuthState } from './auth.types';
import { ADMIN } from '../account/Constants';
import { CLEAR_IMPERSONATION } from '../admin/admin.constants';
import { AdminState } from '../admin/admin.types';
import { isCapsLockPressed } from '../util/capslock.check.util';
import { mmsStore } from '../app/app.config';
import { TOKEN_RENEW } from '../idle/Constants';
import { DIFFERENT_ACCOUNT_LOGIN, SAME_ACCOUNT_LOGIN, EMAIL_OR_PWD_INCORRECT } from './auth.constants';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import { renderBrowserWarningMessage, isBrowserNotSupported } from '../commons/BrowserWarning';
import { isAdminAccount } from '../admin/Util';
import { BusinessResponse } from '../business/business.types';
import './login.css';
import { LOG_IN_PIC, M3_LOGO } from '../navigation/Constants';
import AuthFooter from './auth-footer.component';

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly isReLogin: boolean
  readonly onClickLogin?: (email: string, password: string, fromEmailVerification: boolean) => Promise<Response>
  readonly forgotPassword?: (email: string) => Promise<Response>
  readonly toDashboard?: () => void
  readonly toAdminDashboard?: () => void
  readonly toLogin?: () => void
  readonly fromEmailVerification: boolean
  readonly getBusiness?: () => Promise<BusinessResponse>
};

type States = {
  readonly email: string
  readonly emailError: string
  readonly password: string
  readonly passwordError: string
  readonly loginErrorMessage: string
  readonly forgotPasswordClicked: boolean
  readonly capsLockWarningMessage: string
  readonly isBrowserNotSupported: boolean
};

// TODO: in the interest of time, all the input fields were put into this one
// component. Break the form elements out to more granular reusable components.
class LoginComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      email: '',
      password: '',
      emailError: '',
      passwordError: '',
      loginErrorMessage: '',
      forgotPasswordClicked: false,
      capsLockWarningMessage: '',
      isBrowserNotSupported: false
    };

    this.onEmailChange = this.onEmailChange.bind(this);
    this.onPasswordChange = this.onPasswordChange.bind(this);
    this.handleForgotPasswordClick = this.handleForgotPasswordClick.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
  }

  componentDidMount() {
    const { authState, toDashboard, toAdminDashboard, adminState, isReLogin, fromEmailVerification } = this.props;

    this.setState(
      { isBrowserNotSupported: isBrowserNotSupported() },
      () => {
        if (this.allReadyToGoToDashboard(authState, adminState) && !isReLogin && !fromEmailVerification) {
          toDashboard();
        } else if (this.allReadyToGoToAdminDashboard(authState) && !isReLogin && !fromEmailVerification) {
          toAdminDashboard();
        }
      }
    );
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps && !isAuthTokenExpired()) {
      const { authState } = nextProps;

      switch (authState.statusCode) {
        case 423:
          return this.setState({
            loginErrorMessage: 'Account is locked due to multiple login failures. Please contact the support'
          });
        case 500:
          return this.setState({
            loginErrorMessage: 'Something went wrong on our side. Please contact the support'
          });
        case 400:
          return this.setState({
            loginErrorMessage: EMAIL_OR_PWD_INCORRECT
          });
        case 404:
          return this.setState({
            loginErrorMessage: EMAIL_OR_PWD_INCORRECT
          });
        case 403:
          return this.setState({
            loginErrorMessage: 'This account has been cancelled. Please contact the support.'
          });
        default:
          return this.setState({
            loginErrorMessage: ''
          });
      }
    }
  }

  render() {
    const { isReLogin, fromEmailVerification } = this.props;

    return (
      (!isReLogin && !fromEmailVerification) ?
        (
          <div className="row">
            <div className="fl w-50 col">
              <div className="img-container">
                <img src={LOG_IN_PIC} width="100%" />
                <hr className="login-hr" />
                <img className="login-logo" src={M3_LOGO} />
                <div className="auth-footer">
                  <AuthFooter />
                </div>
              </div>
            </div>
            <div className="fl w-50 login-bg col">
              <div className="fl w-40 input-container" >
                <div className="pb3">
                  <div className="input-label">E-mail</div>
                  <div className="row no-gutters">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email"
                      value={this.state.email}
                      onChange={this.onEmailChange}
                      onKeyPress={this.onKeyPress}
                    />
                  </div>
                  <div>
                    <div className="row no-gutters">
                      <div className="red animated fadeIn">
                        {this.state.emailError}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pt3 pb3">
                  <div className="row no-gutters">
                    <div className="input-label">Password</div>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Password"
                      value={this.state.password}
                      onChange={this.onPasswordChange}
                      onKeyPress={this.onKeyPress}
                    />
                  </div>
                </div>
                <div>
                  <div className="row no-gutters">
                    <div className="red animated fadeIn">
                      {this.state.loginErrorMessage}
                    </div>
                  </div>
                </div>
                <div className="pt3">
                  <div className="col">
                    <button
                      type="button"
                      className="btn btn-block btn-lg"
                      onClick={() => this.toggleLoginAction(mmsStore)}
                      style={{ backgroundColor: '#f8e008' }}
                    >
                      Sign in
                    </button>
                  </div>
                </div>
                <div>
                  <div className="row no-gutters mt2 content-center d-inline-block fr">
                    <a
                      style={{ color: '#A4A7AA' }}
                      href="#"
                      onClick={this.handleForgotPasswordClick}
                    >
                      forgot password ?
                    </a>
                    <div className="green animated fadeIn">
                      {this.state.forgotPasswordClicked ? 'Email is sent!' : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) :
        (
          <AuthLayoutComponent
            isReLogin={isReLogin}
            fromEmailVerification={fromEmailVerification}
          >
            {renderBrowserWarningMessage(this.state.isBrowserNotSupported)}
            <div className="row no-gutters">
              <div className="col-12">
                <div
                  className={
                    (this.state.emailError ? 'has-danger ' : '') + 'input-group mb-2 mr-sm-2 mb-sm-0'
                  }
                >
                  <div className="input-group-addon mt2 mr2">
                    <i
                      className="material-icons f3 yellow"
                      style={{ fontSize: '17px' }}
                    >
                      email
                    </i>
                  </div>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email"
                    value={this.state.email}
                    onChange={this.onEmailChange}
                    onKeyPress={this.onKeyPress}
                  />
                </div>
              </div>
              {this.renderEmailError()}
            </div>
            <div className="row no-gutters mt2">
              <div className="col-12">
                <div className="input-group mb-2 mr-sm-2 mb-sm-0">
                  <div className="input-group-addon mt2 mr2">
                    <i
                      className="material-icons f3 yellow"
                      style={{ fontSize: '17px' }}
                    >
                      lock
                    </i>
                  </div>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Password"
                    value={this.state.password}
                    onChange={this.onPasswordChange}
                    onKeyPress={this.onKeyPress}
                  />
                </div>
              </div>
            </div>
            <div className="row no-gutters mt2 content-center d-inline-block">
              <a
                className="yellow"
                href="#"
                onClick={this.handleForgotPasswordClick}
              >
                forgot password ?
              </a>
              <div className="green animated fadeIn">
                {this.state.forgotPasswordClicked ? 'Email is sent!' : ''}
              </div>
            </div>
            {this.renderLoginErrorMessage()}
            {this.renderCapsLockWarningMessage()}
            <div className="row mt3">
              <div className="col">
                <button
                  type="button"
                  className="btn btn-block btn-lg"
                  onClick={() => this.toggleLoginAction(mmsStore)}
                  style={{ backgroundColor: '#f8e008' }}
                >
                  Sign in
                </button>
              </div>
            </div>
          </AuthLayoutComponent>
        )
    );
  }

  renderCapsLockWarningMessage = (): JSX.Element => {
    return (
      <div className="red animated fadeIn">
        {this.state.capsLockWarningMessage}
      </div>
    );
  }

  renderLoginErrorMessage = (): JSX.Element => {
    return (
      <div className="red animated fadeIn">
        {this.state.loginErrorMessage}
      </div>
    );
  }

  renderEmailError = (): JSX.Element => {
    if (this.state.emailError) {
      return (
        <div className="tc w-100">
          <p className="mv1 red f7">{this.state.emailError}</p>
        </div>
      );
    } else {
      return null;
    }
  }

  handleForgotPasswordClick = (): void => {
    const { email, emailError } = this.state;
    const { forgotPassword } = this.props;

    if (email === '') {
      return this.setState({
        emailError: 'Enter Email and then click forgot password again.'
      });
    } else if (emailError === '') {
      return this.setState({ forgotPasswordClicked: true }, () => forgotPassword(email));
    }
  }

  toggleLoginAction = (store): void => {
    const { email, password, emailError, passwordError } = this.state;
    const { onClickLogin, fromEmailVerification, toDashboard, toAdminDashboard, getBusiness } = this.props;

    if (email && password) {
      if (emailError || passwordError) {
        this.setState({ loginErrorMessage: EMAIL_OR_PWD_INCORRECT });
      } else if (this.props.authState.email !== this.state.email) {
        store.dispatch({ type: DIFFERENT_ACCOUNT_LOGIN });
        store.dispatch({ type: CLEAR_IMPERSONATION });
        onClickLogin(email, password, fromEmailVerification)
          .then(res => {
            switch (this.props.authState.statusCode) {
              case 200:
                mmsStore.dispatch({ type: TOKEN_RENEW });
                if (isAdminAccount(this.props.authState)) {
                  toAdminDashboard();
                } else {
                  getBusiness();
                  toDashboard();
                }
                break;
              default:
                break;
            }
          });
        return;
      } else {
        store.dispatch({ type: SAME_ACCOUNT_LOGIN });
        store.dispatch({ type: CLEAR_IMPERSONATION });
        onClickLogin(email, password, fromEmailVerification)
          .then(res => {
            switch (this.props.authState.statusCode) {
              case 200:
                mmsStore.dispatch({ type: TOKEN_RENEW });
                if (isAdminAccount(this.props.authState)) {
                  toAdminDashboard();
                } else {
                  getBusiness();
                }
                break;
              default:
                break;
            }
          });
      }
    }
  }

  allReadyToGoToDashboard = (authState: AuthState, adminState: AdminState): boolean =>
    (adminState.impersonation !== undefined && adminState.impersonation.authToken !== '') ||
    this.isNotAdminAccount(authState)

  isNotAdminAccount = (authState: AuthState): boolean =>
    authState.statusCode === 200 &&
    authState.authToken !== '' &&
    authState.account !== undefined &&
    authState.account.accountType !== ADMIN

  private allReadyToGoToAdminDashboard = (authState: AuthState): boolean =>
    authState.statusCode === 200 &&
    authState.authToken !== '' &&
    authState.account !== undefined &&
    authState.account.accountType === ADMIN

  private onEmailChange(event: React.FormEvent<HTMLInputElement>) {
    const newEmail = event.currentTarget.value;

    this.setState({
      email: newEmail,
      emailError: isValidEmail(newEmail, 'Please enter a valid email address')
    });
  }

  private onPasswordChange(event: React.FormEvent<HTMLInputElement>) {
    const newPassword = event.currentTarget.value;
    this.setState({ password: newPassword });
  }

  private onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter Key
    if (event.charCode === 13 || event.keyCode === 13) { this.toggleLoginAction(mmsStore); }

    this.setState({ capsLockWarningMessage: isCapsLockPressed(event) ? 'CapsLock is on' : '' });
  }
}

export default LoginComponent;



// WEBPACK FOOTER //
// ./src/auth/login.component.tsx